"use client";

import { isSocialTrafficSource } from "@/components/AdobeDataLayer/utils";
import IconPhone from "@/components/icons/IconPhone";
import { PHONE_NUMBERS } from "@/constants/phone";
import usePhoneNumber from "@/hooks/use-phone-number";
import cn from "@/lib/cn";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import { TAGULAR_ELEMENT } from "@/types/tagular";
import { usePathname } from "next/navigation";

interface PhoneNumberButtonProps extends React.HTMLAttributes<HTMLAnchorElement> {
  fallbackPhone?: string;
  label?: string;
  labelMobile?: string;
  labelTablet?: string;
  labelClassName?: string;
  phoneNumberClassName?: string;
  icon?: React.ReactNode;
  iconClassName?: string;
  tagularProps?: TAGULAR_ELEMENT;
}

const PhoneNumberButton = ({
  fallbackPhone = PHONE_NUMBERS.DEFAULT,
  label = "Call to order:",
  labelMobile,
  labelTablet,
  labelClassName,
  phoneNumberClassName,
  icon,
  iconClassName,
  tagularProps,
  className,
  ...props
}: PhoneNumberButtonProps) => {
  const pathname = usePathname();
  const isSocial = isSocialTrafficSource(pathname);
  const phoneNumber = usePhoneNumber(fallbackPhone);

  return (
    <a
      {...props}
      href={`tel:${phoneNumber}`}
      className={cn(
        "title-tiny-semibold inline-flex items-center gap-2 rounded-full border-2 border-stroke-theme1-base bg-white px-4 py-2 text-theme1-base cursor-pointer no-underline transition-colors hover:bg-neutral-light",
        className
      )}
      onClick={() =>
        fireElementClicked({
          actionOutcome: "phoneNumber",
          elementType: "phoneNumber",
          text: phoneNumber,
          ...tagularProps,
        })
      }
    >
      {icon !== undefined ? (
        icon && <span className={iconClassName}>{icon}</span>
      ) : (
        <IconPhone theme="purple" style="outline" className="size-5 shrink-0" />
      )}
      <span className={cn("whitespace-nowrap", labelClassName)}>
        {labelMobile !== undefined || labelTablet !== undefined ? (
          <>
            <span className="md:hidden">{labelMobile ?? label}</span>
            <span className="hidden md:inline lg:hidden">
              {labelTablet ?? label}
            </span>
            <span className="hidden lg:inline">{label}</span>
          </>
        ) : (
          label
        )}
      </span>
      <span
        className={cn("whitespace-nowrap", phoneNumberClassName)}
        {...(isSocial
          ? {}
          : {
              "data-fuse": true,
              "data-fuse-format": "1-###-###-####",
            })}
        // Fuse modifies the phone text before hydration on BAU routes only.
        suppressHydrationWarning
      >
        {phoneNumber}
      </span>
    </a>
  );
};

export default PhoneNumberButton;
